/**
 * CommunityPage — top-level community (per-creator wall) page.
 *
 * Web port of Flutter's `CommunityScreen`
 * (`lib/presentation/screens/community/community_screen.dart`). Route
 * `/community/:userId`; in-app nav forwards the full `UserEntity` via
 * `location.state` (`Navigator.navigateToCommunity`), the deep-link / refresh
 * path rehydrates the creator from the URL `:userId` param inside `useCommunity`.
 *
 * Page + feature-hook split (`presentation.md` § "Page + feature-hook split"):
 * the page owns `useBasePage`, the `screenKey` mint, `useParams`, the
 * `location.state` snapshot, the Redux `currentUser` gate, the app bar, and the
 * sheet/share invocations; `useCommunity` (Task 10) owns state + lifecycle
 * side-effects. The page wires the hook's `onPageInit` / `onPageVisible` /
 * `onPageHidden` / `onPageDispose` into its own `useBasePage` (mirrors
 * `ChatPage.tsx:116-123`).
 *
 * Body branch order (matters — `community_screen.dart:909-913`):
 *   `!hasAccess` → subscribe CTA → else `creatorBlocked` → blocked placeholder
 *   → else the messages list + composer (+ empty-state / unread-pill /
 *   scroll-down overlays). The owner always passes the access gate.
 *
 * Window-scrolled shell mirrors `ChatPage`: a sticky app bar (top inset) and a
 * sticky composer (bottom inset) in document flow, with the overlays pinned to a
 * `position: fixed` inter-bar band so they stay glued to the visible strip while
 * the document scrolls underneath. Insets are measured (not hard-coded) because
 * the composer height is dynamic.
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';

import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';

import { useBasePage } from '@/presentation/hooks/useBasePage';
import { useKeyboardInset } from '@/presentation/hooks/useKeyboardInset';
import { useMeasuredHeight } from '@/presentation/hooks/useMeasuredHeight';
import { useKeepAlive } from '@/presentation/routing/RouteStack';
import { generateScreenKey } from '@/presentation/routing/screenKeyUtils';
import { Navigator } from '@/presentation/routing/Navigator';

import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import { SubscribeUserSheet } from '@/presentation/feature/userDetails/components/subscribeUserSheet/SubscribeUserSheet';
import { ConfirmSheet } from '@/presentation/components/common/sheet/confirm/ConfirmSheet';
import { launchLiveStreaming } from '@/presentation/feature/liveStreaming/utils/launchLiveStreaming';
import { launchBroadcasterGoLive } from '@/presentation/feature/liveStreaming/utils/launchBroadcasterGoLive';
import { isWatchingLive, markWatchingLive } from '@/presentation/feature/liveStreaming/utils/liveAudiencePresence';
import { useIsBroadcastingLive } from '@/presentation/feature/liveStreaming/hooks/useIsBroadcastingLive';
import { qaAttr } from '@/presentation/utils/qaAttr';

import type { RootState } from '@/presentation/store';
import type { UserEntity } from '@/domain/entities/user/userEntity';
import type { CommunityChatMessageEntity } from '@/domain/entities/community/communityChatMessageEntity';
import { CommunityChatMessageType } from '@/domain/enums/community/communityChatMessageType';
import { VIDEO_CONSTANTS } from '@/domain/constants/videoConstants';

import { useCommunity } from './hooks/useCommunity';
import { useCommunityShare } from './hooks/useCommunityShare';
import { CommunityAppBar } from './components/CommunityAppBar';
import { CommunityMessagesList, type CommunityMessagesListHandle } from './components/CommunityMessagesList';
import { CommunityComposer } from './components/CommunityComposer';
import { CommunityEmptyState } from './components/CommunityEmptyState';
import { CommunitySubscribeCTA } from './components/CommunitySubscribeCTA';
import { CommunityUnreadPill } from './components/CommunityUnreadPill';

export const CommunityPage = () => {
  const { userId: userIdParam } = useParams<{ userId: string }>();
  const location = useLocation();
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  const currentUser = useSelector((state: RootState) => state.auth.user);

  // Note-2 broadcaster guard (Task 12): reactively read the cross-tab
  // broadcaster-presence ledger for the CURRENT user. True while this user is
  // already broadcasting a LIVE session (in this or any same-origin tab) — set by
  // `useLiveStreaming.handleChannelConnected` on the broadcaster side, cleared by
  // its `onPageDispose` on every exit path. The hook re-evaluates on the cross-tab
  // `storage` event + tab-visibility, so ending a broadcast in another tab
  // re-enables the composer go-live here without a poll. Since only the wall owner
  // sees go-live and the owner is the only one who can broadcast their OWN wall,
  // `isBroadcastingLive(ownUid)` is the exact "I already have a live running"
  // signal for this composer.
  const isBroadcasting = useIsBroadcastingLive(currentUser?.firebaseId);

  // Canonical screen-key wiring — captured ONCE on mount (mirrors `ChatPage`).
  const { currentScreenKeyRef } = useKeepAlive();
  const screenKeyRef = useRef<string>(
    currentScreenKeyRef.current || generateScreenKey(location.pathname),
  );
  const screenKey = screenKeyRef.current;

  // Snapshot of `location.state` (the full `UserEntity` carried by
  // `Navigator.navigateToCommunity`), taken ONCE at mount. `null` on the
  // deep-link / refresh path — the hook rehydrates from the URL `:userId` param
  // in that case.
  const initialCreatorUser = useMemo<UserEntity | null>(() => {
    const state = location.state as UserEntity | undefined;
    if (state && state.firebaseId && state.firebaseId === userIdParam) {
      return state;
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Like-failure toast (`communit_message_widget.dart:164` Fluttertoast) —
  // page-owned so `useCommunity` stays free of UI invocations
  // (`presentation.md` § Page + feature-hook split; same principle as
  // `handleSubscribePress` below). The hook invokes this before reverting the
  // optimistic count.
  const handleLikeMessageError = useCallback((): void => {
    toast.error(l10n.community.errorLikingMessage);
  }, [l10n]);

  const community = useCommunity({
    ownerIdParam: userIdParam ?? '',
    initialCreatorUser,
    onLikeMessageError: handleLikeMessageError,
  });

  // The live-message id the CURRENT user is broadcasting, captured when they
  // launch a go-live from THIS wall (`handleGoLive` below). Drained on the
  // pop-back path to optimistically flip that tile to `isLive: false` — the web
  // wiring equivalent of Flutter's `onBroadcasterClosedChannel: endLiveMessage`
  // callback (`community_screen.dart:863`), which flips the broadcaster's own
  // list entry (`:653-659`). Only the broadcaster path ever sets this ref, so an
  // audience "Join Live" return (via `handleLiveItemTap`) never flips anything —
  // matching Flutter, where only the broadcaster's own screen flips its own list.
  const pendingBroadcastMessageIdRef = useRef<string | null>(null);

  // Page-only `useBasePage` — forwards each lifecycle phase into the hook's
  // exposed (stable `useCallback`) handler (`ChatPage.tsx:116-123`).
  useBasePage({
    screenKey,
    onInit: community.onPageInit,
    onPopNext: () => {
      community.onPageVisible();
      // Broadcaster returned from a live session THEY started on this wall —
      // optimistically flip the just-ended tile (finding 1). The community page
      // is keep-alive, so the broadcaster's own end write never re-renders their
      // own tile via the listener echo; this local flip mirrors Flutter's
      // `endLiveMessage` own-list flip.
      const endedId = pendingBroadcastMessageIdRef.current;
      if (endedId !== null) {
        community.markLiveMessageEnded(endedId);
        pendingBroadcastMessageIdRef.current = null;
      }
    },
    onPushNext: community.onPageHidden,
    onPop: community.onPageHidden,
    onDispose: community.onPageDispose,
  });

  // Sticky-chrome insets — same contract as `ChatPage`: the app bar is sticky
  // at the top, the composer sticky at the bottom; their measured heights
  // position the fixed overlay layer and feed `VirtualList`'s
  // `reversedBottomPinInset`. Declared before the loading gate to satisfy the
  // Rules of Hooks; the callback refs attach once the wrappers mount post-gate.
  const [topInset, topInsetRef] = useMeasuredHeight();
  const [bottomInset, bottomInsetRef] = useMeasuredHeight();
  const keyboardInset = useKeyboardInset();
  const bottomChromeInset = bottomInset + keyboardInset;

  // Imperative handle into the messages list — drives the post-send snap and
  // the unread-pill / scroll-down tap.
  const messagesListRef = useRef<CommunityMessagesListHandle>(null);

  // Back → pop the route, or fall back to the feed when there is no history
  // (deep-link entry). Mirrors `ChatPage.handleBack`.
  const handleBack = useCallback((): void => {
    Navigator.goBackOr(() => Navigator.navigateToFeed());
  }, []);

  // Share the creator's profile — the web `_onShareSocialUser`
  // (`community_screen.dart:865-889`). The flow (build share content + props,
  // prefer `navigator.share` behind the protective sheet, fall back to
  // `ShareSocialSheet.show`) lives in `useCommunityShare`. Wired to both the
  // app-bar share button and the owner empty-state "Share to get subscribers"
  // button.
  const { handleShare } = useCommunityShare(community.creatorUser, screenKey);

  const handleSharePress = useCallback((): void => {
    void handleShare();
  }, [handleShare]);

  // Open the subscribe sheet for the creator (the web `_onSubscribeTapped`,
  // `community_screen.dart:891-901`). Page-owned so the hook stays free of UI
  // invocations — the page has `screenKey` and forwards the resolved
  // `creatorUser` so the sheet does not re-fetch it (mirrors
  // `ChatPage.handleSubscribePress`).
  const handleSubscribePress = useCallback((): void => {
    SubscribeUserSheet.show({
      targetFirebaseId: userIdParam ?? '',
      preloadedUser: community.creatorUser,
      screenKey,
    });
  }, [userIdParam, community.creatorUser, screenKey]);

  // Tapping the unread pill / scroll-down button: snap the list to the newest
  // message AND flush the unread bucket (Flutter `_itemScrollController.scrollTo
  // (index: 0, …)` + clear, `community_screen.dart:1167-1173`, `:845-853`).
  const handleScrollDown = useCallback((): void => {
    messagesListRef.current?.scrollToBottom();
    community.onScrolledToBottom();
  }, [community]);

  // Tap a ready image message → open the full-screen viewer (the web
  // `DefaultMediaPage`, a prompt-directed deviation from Flutter's content-list
  // pager — `community_screen.dart:714-744`, task-prompt line 25). Community
  // media is unencrypted, so the absolute URL comes straight off the entity
  // (`mediaInfo.path`, remapper-derived via `joinDefaultBucketUrl`). Guard on a
  // present path + not-uploading, mirroring the card's own no-op
  // (`community_message_item.dart:403-405`); the aspect-ratio fallback matches
  // Flutter (`:723-725` ⇔ `9/16`).
  const handleMediaTap = useCallback((message: CommunityChatMessageEntity): void => {
    const mediaInfo = message.mediaInfo;
    if (mediaInfo === undefined || mediaInfo.path === undefined || mediaInfo.isUploading) {
      return;
    }
    // Video → the dedicated encrypted-HLS community playback page
    // (`CommunityMediaPage`, Task 15). The message travels via `location.state`;
    // the page initializes via `InitializeCommunityVideoUseCase` and plays
    // through the SAME `InlineVideoPlayer` chat uses. `mediaInfo.path` is the
    // Task-5 done-marker (a legacy-pipeline doc also carries `path`; its
    // playback failure surfaces via the page's error state).
    if (message.type === CommunityChatMessageType.video) {
      Navigator.navigateToCommunityMedia(message.id, { message });
      return;
    }
    Navigator.navigateToDefaultMedia(
      mediaInfo.path,
      mediaInfo.aspectRatio ?? VIDEO_CONSTANTS.DEFAULT_ASPECT_RATIO,
    );
  }, []);

  // Tap "Join Live" on a live message tile → confirm "Join LIVE?" then launch the
  // audience live session (Flutter `_onLiveItemTap`, `community_screen.dart:700-718`
  // → `_joinLiveStream` audience branch, `:834-865`). The page owns the confirm
  // sheet + navigation (`presentation.md` § Page + feature-hook split); the tile
  // stays presentational. `broadcastingInfo` is read directly off the typed
  // message entity (a `MessageBroadcastingInfoEntity` populated by the domain
  // remapper) — no remapper call from the page. `creatorUser` is the
  // wall owner = the broadcaster channel (parity `:857-861`), and the join is
  // gated on `isLive` (belt-and-suspenders with the tile's disabled CTA, parity
  // `:519-522` / `:712`).
  const handleLiveItemTap = useCallback(async (message: CommunityChatMessageEntity): Promise<void> => {
    const broadcaster = community.creatorUser;
    if (broadcaster === null) {
      return;
    }
    // Owner guard (finding 1), belt-and-suspenders with the tile's disabled CTA:
    // never let the broadcaster join their OWN in-progress session as audience
    // (which would inflate their own watcher count). The wall owner IS the
    // broadcaster, so bail when the current user is the broadcaster.
    if (currentUser !== null && broadcaster.firebaseId === currentUser.firebaseId) {
      return;
    }
    const info = message.broadcastingInfo;
    if (info === undefined || !info.isLive) {
      return;
    }
    // Cross-tab dedupe (finding 2, web-specific): if this user is already watching
    // THIS session in another same-origin tab, do NOT navigate — blocking here
    // (rather than merely skipping the watcher +1) honors both constraints, since
    // not navigating means no second Agora token mint. `localStorage` is shared
    // synchronously across tabs, so this is a pure local read (no DB query). The
    // owner guard above already excludes the broadcaster, so `currentUser` is a
    // non-null subscriber on this path.
    if (currentUser !== null && isWatchingLive(currentUser.firebaseId, message.id)) {
      toast(l10n.community.liveStreamingPage.alreadyWatchingToast);
      return;
    }
    const response = await ConfirmSheet.show({
      title: l10n.community.liveStreamingPage.joinConfirmTitle,
      confirmText: l10n.community.liveStreamingPage.joinConfirmDescription,
      okText: l10n.common.yes,
      cancelText: l10n.common.cancel,
      screenKey,
    });
    if (!response) {
      return;
    }
    // Mark presence right before navigating (single-threaded synchronous
    // check-then-set within the tab keeps the race window negligible). This is the
    // write the OTHER tab's gate above reads; `useLiveStreaming.onPageDispose`
    // clears it on leave / session end. `currentUser` is the subscriber (owner
    // guard above excludes the broadcaster).
    if (currentUser !== null) {
      markWatchingLive(currentUser.firebaseId, message.id);
    }
    await launchLiveStreaming({
      broadcasterFirebaseId: broadcaster.firebaseId,
      broadcasterName: broadcaster.name,
      broadcasterStreamingId: info.broadcasterStreamingId,
      liveStreamingMessageId: message.id,
      asBroadcaster: false,
      l10n,
    });
  }, [community.creatorUser, currentUser, screenKey, l10n]);

  // Owner-only in-line "GO LIVE" composer button (Flutter `_goLive`,
  // `community_screen.dart:815-832`; `GoLiveButtonWidget`, `:1359-1367`). The
  // page owns the confirm sheet + navigation (`presentation.md` § Page +
  // feature-hook split); the composer stays dumb and only surfaces the button
  // when `isCurrentUser`. Reuses the shared `launchBroadcasterGoLive` (the same
  // helper `SelectContentTypeSheetContent` calls) — confirm → mint → launch as
  // broadcaster. The broadcaster IS `currentUser` (non-null here: the render is
  // gated on `currentUser !== null` above).
  const handleGoLive = useCallback(async (): Promise<void> => {
    if (currentUser === null) {
      return;
    }
    // Remember the minted live-message id so the pop-back path can optimistically
    // flip its own tile to `isLive: false` when the broadcast ends (finding 1).
    // `undefined` when the confirm was declined — nothing launched, so leave the
    // ref untouched.
    const launchedId = await launchBroadcasterGoLive({ currentUser, screenKey, l10n });
    if (launchedId !== undefined) {
      pendingBroadcastMessageIdRef.current = launchedId;
    }
  }, [currentUser, screenKey, l10n]);

  // Post-send auto-scroll: `useCommunity.scrollToTopToken` ticks on every
  // optimistic insert in `sendText`; on change snap the reversed `VirtualList`
  // viewport to the newest message (Flutter `scrollTo(index: 0, …)`,
  // `community_screen.dart:539-543`). Skip the initial token=0 firing so we
  // don't fight the list's own first-mount bottom anchor.
  const hasSeenInitialTokenRef = useRef<boolean>(false);
  useEffect(() => {
    if (!hasSeenInitialTokenRef.current) {
      hasSeenInitialTokenRef.current = true;
      return;
    }
    const handle = window.requestAnimationFrame(() => {
      messagesListRef.current?.scrollToBottom();
    });
    return () => window.cancelAnimationFrame(handle);
  }, [community.scrollToTopToken]);

  // Loading gate — auth-hydrate OR hook rehydrate in flight
  // (`presentation.md` loading-gate rule; `community_screen.dart:1116-1128`
  // first-fetch indicator). The creator may be null on the deep-link path until
  // rehydration completes.
  if (community.isFirstLoad || currentUser === null || community.creatorUser === null) {
    return (
      <div
        {...qaAttr({ id: 'community-page', status: 'loading' })}
        style={{
          width: '100%',
          height: '100vh',
          backgroundColor: theme.background,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <ExpauseLoadingIndicator />
      </div>
    );
  }

  // --- Resolved render data (gated on creator + current user) ---
  const creator = community.creatorUser;

  // App-bar title (`appBarTitle`, `community_screen.dart:1047-1057`): "My
  // Community" for the owner, else the apostrophe-`s` possessive keyed off the
  // creator's first name.
  let title: string;
  if (community.isCurrentUser) {
    title = l10n.community.titleOwn;
  } else if (creator.firstName.endsWith('s')) {
    title = l10n.community.titlePossessiveApostrophe.replace('{firstName}', creator.firstName);
  } else {
    title = l10n.community.titlePossessive.replace('{firstName}', creator.firstName);
  }

  // Body branch order (`community_screen.dart:909-913`): no-access → blocked →
  // list. Drives both the rendered body and the page-root `data-qa-status`.
  const showSubscribeCTA = !community.hasAccess;
  const showBlocked = !showSubscribeCTA && community.isCreatorBlocked;
  const showList = !showSubscribeCTA && !showBlocked;

  const pageQaStatus = showSubscribeCTA
    ? 'no-access'
    : showBlocked
      ? 'blocked'
      : 'ready';

  // Empty-state overlay gate (`emptyList`, `community_screen.dart:1421`):
  // `!_hasMore && _messages.isEmpty`.
  const showEmptyState = !community.hasMore && community.messages.length === 0;

  // Unread-pill preview — the NEWEST unread item (the bucket appends in arrival
  // order). Text → the raw body; non-text branches use the Task-8 templates
  // keyed off the unread item's AUTHOR name (`community_screen.dart:1135-1149`).
  const newestUnread =
    community.unreadItems.length === 0
      ? null
      : community.unreadItems[community.unreadItems.length - 1];
  let unreadPreview: string | null = null;
  if (newestUnread !== null) {
    const authorName = newestUnread.messageUsersInfo.userName;
    switch (newestUnread.type) {
      case CommunityChatMessageType.text:
        unreadPreview = newestUnread.text;
        break;
      case CommunityChatMessageType.image:
        unreadPreview = l10n.community.unread.photo.replace('{name}', authorName);
        break;
      case CommunityChatMessageType.video:
        unreadPreview = l10n.community.unread.video.replace('{name}', authorName);
        break;
      case CommunityChatMessageType.gift:
        unreadPreview = l10n.community.unread.gift.replace('{name}', authorName);
        break;
      default:
        // `live` and any future type → the "started a Live streaming" template
        // (`community_screen.dart:1146-1148`).
        unreadPreview = l10n.community.unread.live.replace('{name}', authorName);
        break;
    }
  }

  return (
    // Window-scrolled shell (mirrors `ChatPage`): no `100vh` / `overflow: hidden`
    // lock — the document scrolls, the sticky app bar / composer sit in flow.
    <div
      {...qaAttr({ id: 'community-page', status: pageQaStatus })}
      style={{
        position: 'relative',
        minHeight: '100vh',
        width: '100%',
        backgroundColor: theme.background,
      }}
    >
      {/* Sticky app bar (`appBar`, `community_screen.dart:1043-1073`). No
          `paddingTop: responsive.safeArea.top` here: `CommunityAppBar` renders
          the shared `ExpauseAppBar`, whose own
          `calc(topBar.height + safeArea.top)` box folds in the top inset — so
          this wrapper's measured height (the `topInset` the list and the fixed
          overlay band consume) is unchanged, and the inset applies once. */}
      <div
        ref={topInsetRef}
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 50,
          backgroundColor: theme.background,
        }}
      >
        <CommunityAppBar title={title} onBack={handleBack} onShare={handleSharePress} />
      </div>

      {/* Body. Branch order (`community_screen.dart:909-913`):
            no-access → blocked → list. */}
      {showSubscribeCTA && (
        // Subscribe placeholder (`subscribeToExclusiveContentWidget`,
        // `community_screen.dart:979-1023`). Filled to the inter-bar band.
        // The `safeArea.top` term below is deliberately left in place: it is
        // already inside `topInset`, a pre-existing cosmetic over-subtraction
        // that the app-bar migration keeps byte-identical (same for the blocked
        // placeholder) rather than silently changing this band's height.
        <div
          style={{
            minHeight: `calc(100vh - ${topInset}px - ${responsive.safeArea.top})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CommunitySubscribeCTA
            creatorName={creator.name}
            onSubscribePress={handleSubscribePress}
          />
        </div>
      )}

      {showBlocked && (
        // Blocked placeholder (`_userBlockedWidget`,
        // `community_screen.dart:1025-1041`).
        <div
          {...qaAttr({ id: 'community-blocked' })}
          style={{
            minHeight: `calc(100vh - ${topInset}px - ${responsive.safeArea.top})`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: responsive.spacing.lg,
            backgroundColor: theme.background,
            boxSizing: 'border-box',
          }}
        >
          <span
            style={{
              fontSize: responsive.font.lg,
              fontWeight: responsive.fontWeight.regular,
              color: theme.text.primary,
              textAlign: 'center',
              maxWidth: '80%',
            }}
          >
            {l10n.community.userBlocked.replace('{name}', creator.name)}
          </span>
        </div>
      )}

      {showList && (
        <>
          {/* Messages list — flows in the document (window-scrolled). The flow
              wrapper reserves the inter-bar viewport band so the in-flow sticky
              composer stays bottom-pinned even when the list collapses to ~0
              height (empty wall). Mirrors `ChatMessagesRegion`. */}
          <div
            style={{
              minHeight: `calc(100vh - ${topInset}px - ${bottomChromeInset}px)`,
            }}
          >
            <CommunityMessagesList
              ref={messagesListRef}
              qa={{ id: 'community-messages' }}
              messages={community.messages}
              currentUserId={currentUser.firebaseId}
              onLoadMore={community.onLoadMore}
              onScrolledToBottom={community.onScrolledToBottom}
              onScrolledAway={community.onScrolledAway}
              onReplyTap={community.setReplyTarget}
              onLikeTap={community.likeMessage}
              onMediaTap={handleMediaTap}
              onLiveItemTap={handleLiveItemTap}
              reversedBottomPinInset={{ top: topInset, bottom: bottomChromeInset }}
            />
          </div>

          {/* Fixed overlay layer — spans the visible inter-bar band between the
              sticky app bar and the sticky composer. Overlays are `absolute`
              against THIS layer so they stay glued to the viewport while the
              document scrolls underneath. `pointerEvents: 'none'` so taps fall
              through to the list; interactive overlays re-enable it on their
              own child. */}
          <div
            style={{
              position: 'fixed',
              top: topInset,
              bottom: bottomChromeInset,
              left: 0,
              right: 0,
              pointerEvents: 'none',
              zIndex: 40,
            }}
          >
            {/* Empty-state placeholder (`emptyList`,
                `community_screen.dart:1419-1472`). Interactive (owner share
                button) → re-enable pointer events. */}
            {showEmptyState && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  zIndex: 1,
                  pointerEvents: 'auto',
                }}
              >
                <CommunityEmptyState
                  isCurrentUser={community.isCurrentUser}
                  creatorName={creator.name}
                  onSharePress={handleSharePress}
                />
              </div>
            )}

            {/* Unread pill / scroll-down button (`unreadMessageWidget` /
                `scrolledUpWidget`, `community_screen.dart:1130-1238`).
                Bottom-pinned + interactive → re-enable pointer events. */}
            <div
              style={{
                position: 'absolute',
                left: 0,
                right: 0,
                bottom: responsive.spacing.lg,
                zIndex: 4,
                pointerEvents: 'auto',
              }}
            >
              <CommunityUnreadPill
                unreadPreview={unreadPreview}
                unreadBannerVisible={community.unreadBannerVisible}
                isScrolledUp={community.isScrolledUp}
                unreadCount={community.unreadItems.length}
                onScrollDown={handleScrollDown}
              />
            </div>
          </div>

          {/* Sticky composer (`bottomInputActions` / `inputWidget`,
              `community_screen.dart:1240-1379`). Self-hides during first load
              (already gated above); folds in `responsive.safeArea.bottom`. */}
          <div
            ref={bottomInsetRef}
            style={{
              position: 'sticky',
              bottom: keyboardInset,
              zIndex: 50,
              paddingBottom: responsive.safeArea.bottom,
              backgroundColor: theme.background,
            }}
          >
            <CommunityComposer
              isFirstLoad={community.isFirstLoad}
              isCurrentUser={community.isCurrentUser}
              hasMore={community.hasMore}
              hasNoMessage={community.messages.length === 0}
              messageToReply={community.messageToReply}
              onSendText={community.sendText}
              onClearReply={() => community.setReplyTarget(null)}
              screenKey={screenKey}
              onSendImage={community.sendImage}
              onSendVideo={community.sendVideo}
              onGoLive={handleGoLive}
              goLiveDisabled={isBroadcasting}
            />
          </div>
        </>
      )}
    </div>
  );
};
